import React from 'react';
import {
  areHandlersIntersecting,
  getClosestHandlerToClick,
  getIntersectionRelativeX,
  getIntersectionValue,
  getNumericValue,
  getProgressBarStyle,
  getRelativeX,
  getRelativeXFromValue,
  getValuesInValidRange,
} from './helpers';
import {
  HandlerType,
  IDoubleRangeInputEvent,
  ScaleType,
  SliderPositionsType,
  SliderValueType,
} from './types';

import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {checkValidMinMaxProps, checkValidValues} from './errors';

const DEFAULT_STEP = 1;

interface IProps {
  min: number;
  max: number;
  scale: ScaleType;
  /**
   * Granularity of the range slider's values
   * This is inspired by the step attribute from HTML's input element and works similiar for positive integers:
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/range#step
   * */
  step?: number;
  values: SliderValueType;
  intersectionPercentage: number;
  /**
   * Intended to keep track of UI. It triggers every time
   * the values are updated.
   */
  onChange?: (ev: IDoubleRangeInputEvent) => void;
  /**
   * Intended to perform side effects without throttling.
   * Is triggered whenever the user releases a handler.
   */
  onApply?: (ev: IDoubleRangeInputEvent) => void;
  transformLabel: (value: number) => string;
  showLabels?: boolean;
  className?: string;
}

interface IState {
  values: SliderValueType;
}

export class DoubleRangeInput extends React.PureComponent<IProps, IState> {
  // @ts-ignore
  private containerRef: React.RefObject<any>;
  private isLeftHandlerDown = false;
  private isRightHandlerDown = false;
  private isSliderClick = false;
  // @ts-ignore
  private left: number;
  // @ts-ignore
  private width: number;

  static defaultProps = {
    transformLabel: (value: number) => String(value),
    intersectionPercentage: 0.05,
    scale: 'linear',
  };

  constructor(props: IProps) {
    super(props);
    this.containerRef = React.createRef();
    this.initStatefull();
  }

  componentDidMount() {
    document.addEventListener('mousemove', this.handleHandlerMouseMove);
    document.addEventListener('mouseup', this.handleHandlerMouseUp);
    document.addEventListener('touchend', this.handleHandlerTouchEnd);
    document.addEventListener('touchmove', this.handleHandlerTouchMove);
    this.adjustValuesToValidRange();
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleHandlerMouseMove);
    document.removeEventListener('mouseup', this.handleHandlerMouseUp);
    document.removeEventListener('touchend', this.handleHandlerTouchEnd);
    document.removeEventListener('touchmove', this.handleHandlerTouchMove);
  }

  componentDidUpdate(prevProps: IProps) {
    const {min, max} = this.props;
    if (prevProps.min !== min || prevProps.max !== max) {
      this.adjustValuesToValidRange();
    }
  }

  initStatefull = (validValue?: SliderValueType) => {
    if (!this.props.values) {
      // slider initialisation
      const {min, max} = this.props;

      const value = validValue ?? this.props.values;
      const minValue = value[0] ? value[0] : min;
      const maxValue = value[1] ? value[1] : max;

      // eslint-disable-next-line
      this.state = {
        values: [minValue, maxValue],
      };
    }
  };

  adjustValuesToValidRange = (): void => {
    if (this.shouldAdjustRange()) {
      const {min, max, step} = this.props;
      const values = this.getValues();

      const intersectionValue = getIntersectionValue({
        componentX: this.left,
        componentWidth: this.width,
        intersectionPercentage: this.props.intersectionPercentage,
        maxValue: max,
        minValue: min,
        scale: this.props.scale,
        step: step || DEFAULT_STEP,
      });

      const validValue = getValuesInValidRange({
        values,
        min,
        max,
        intersectionValue,
      });

      if (!this.props.values) {
        this.setState({values: validValue});
      }
      this.triggerOnChange(validValue, {
        adjustRange: true,
      });
      this.triggerOnApply({
        adjustRange: true,
      });
    }
  };

  shouldAdjustRange = (): boolean => {
    const {min, max} = this.props;
    const [lowValue, highValue] = this.getValues();

    const minValueIsTooLow = lowValue < min;
    const minValueIsTooHigh = lowValue > max;
    const maxValueIsTooLow = highValue < min;
    const maxValueIsTooHigh = highValue > max;

    return (
      minValueIsTooLow ||
      minValueIsTooHigh ||
      maxValueIsTooLow ||
      maxValueIsTooHigh
    );
  };

  triggerOnChange = (
    values: SliderValueType,
    {adjustRange}: {adjustRange?: boolean} = {},
  ): void => {
    const {onChange} = this.props;
    if (typeof onChange === 'function') {
      onChange({
        values,
        adjustRange: Boolean(adjustRange),
      });
    }
  };

  triggerOnApply = ({adjustRange}: {adjustRange?: boolean} = {}): void => {
    const {onApply} = this.props;
    if (typeof onApply === 'function') {
      onApply({
        values: this.getValues(),
        adjustRange: Boolean(adjustRange),
      });
    }
  };

  // assumes at least one handler is clicked!
  currentHandler = (): HandlerType => {
    if (this.isLeftHandlerDown) {
      return 'left';
    }

    if (this.isRightHandlerDown) {
      return 'right';
    }

    throw new Error(
      'currentHandler() was invoked when no handler had been clicked.',
    );
  };

  getHandlerPositions = (): SliderPositionsType => {
    const [lowValue, highValue] = this.getValues();

    const leftHandlerX = getRelativeXFromValue({
      scale: this.props.scale,
      value: lowValue,
      maxValue: this.props.max,
      minValue: this.props.min,
    });
    const rightHandlerX = getRelativeXFromValue({
      scale: this.props.scale,
      value: highValue,
      maxValue: this.props.max,
      minValue: this.props.min,
    });

    return {
      leftHandlerX,
      rightHandlerX,
    };
  };

  updateValue = (relativeX: number, handler: HandlerType): void => {
    let numericValue = getNumericValue({
      scale: this.props.scale,
      relativeX,
      maxValue: this.props.max,
      minValue: this.props.min,
      step: this.props.step || DEFAULT_STEP,
    });

    const value = this.getValues();
    const isLeftHandler = handler === 'left';

    if (isLeftHandler && numericValue >= value[1]) {
      numericValue = value[1] - 100;
    } else if (!isLeftHandler && numericValue <= value[0]) {
      numericValue = value[0] + 100;
    }

    const newValue: SliderValueType = isLeftHandler
      ? [numericValue, value[1]]
      : [value[0], numericValue];

    if (!this.props.values) {
      this.setState({values: newValue}, () =>
        this.triggerOnChange(this.state.values),
      );
    } else {
      this.triggerOnChange(newValue);
    }
  };

  getValues = (): SliderValueType => {
    return this.props.values ? this.props.values : this.state.values;
  };

  calculateNextRelativeX = ({
    eventX,
    handler,
  }: {
    eventX: number;
    handler: HandlerType;
  }): number => {
    const {min, max, step = DEFAULT_STEP} = this.props;

    const relativeX = getRelativeX({
      eventX,
      componentX: this.left,
      componentWidth: this.width,
    });

    const minHandlerDistance = getIntersectionValue({
      componentX: this.left,
      componentWidth: this.width,
      intersectionPercentage: this.props.intersectionPercentage,
      maxValue: max,
      minValue: min,
      step,
    });

    const handlersIntersecting = areHandlersIntersecting({
      scale: this.props.scale,
      relativeX,
      max,
      min,
      values: this.getValues(),
      minHandlerDistance,
      handler,
      step,
    });

    const nextRelativeX = handlersIntersecting
      ? getIntersectionRelativeX({
          scale: this.props.scale,
          values: this.getValues(),
          handler,
          minDistance: minHandlerDistance,
          maxValue: max,
          minValue: min,
          step,
        })
      : relativeX;

    return nextRelativeX;
  };

  updateSliderPosition = () => {
    const {left, width} = this.containerRef.current.getBoundingClientRect();
    this.left = left;
    this.width = width;
  };

  // ========================================
  //           DESKTOP EVENT HANDLERS
  // ========================================
  handleLeftHandlerMouseDown = (): void => {
    this.updateSliderPosition();
    this.isLeftHandlerDown = true;
  };

  handleRightHandlerMouseDown = (): void => {
    this.updateSliderPosition();
    this.isRightHandlerDown = true;
  };

  handleHandlerMouseUp = (): void => {
    const shouldExecute =
      this.isLeftHandlerDown || this.isRightHandlerDown || this.isSliderClick;
    if (!shouldExecute) {
      return;
    }

    this.triggerOnApply();

    this.isLeftHandlerDown = false;
    this.isRightHandlerDown = false;
    this.isSliderClick = false;
  };

  handleHandlerMouseMove = (ev: MouseEvent): void => {
    if (!this.isLeftHandlerDown && !this.isRightHandlerDown) {
      return;
    }

    const handler = this.currentHandler();
    const nextRelativeX = this.calculateNextRelativeX({
      eventX: ev.clientX,
      handler,
    });

    this.updateValue(nextRelativeX, handler);
  };

  handleSliderMouseDown = (ev: React.MouseEvent): void => {
    if (this.isLeftHandlerDown || this.isRightHandlerDown) {
      return;
    }
    this.updateSliderPosition();
    const {leftHandlerX, rightHandlerX} = this.getHandlerPositions();

    const eventRelativeX = getRelativeX({
      eventX: ev.clientX,
      componentX: this.left,
      componentWidth: this.width,
    });
    const handler = getClosestHandlerToClick({
      leftHandlerX,
      rightHandlerX,
      relativeX: eventRelativeX,
    });

    const nextRelativeX = this.calculateNextRelativeX({
      eventX: ev.clientX,
      handler,
    });

    // Update values
    this.isSliderClick = true;
    this.updateValue(nextRelativeX, handler);
  };

  // ========================================
  //           MOBILE EVENT HANDLERS
  // ========================================
  handleLeftHandlerTouchStart = (): void => {
    this.updateSliderPosition();
    this.isLeftHandlerDown = true;
  };

  handleRightHandlerTouchStart = (): void => {
    this.updateSliderPosition();
    this.isRightHandlerDown = true;
  };

  handleHandlerTouchEnd = (): void => {
    const shouldExecute =
      this.isLeftHandlerDown || this.isRightHandlerDown || this.isSliderClick;
    if (!shouldExecute) {
      return;
    }

    this.triggerOnApply();
    this.isRightHandlerDown = false;
    this.isLeftHandlerDown = false;
  };

  handleHandlerTouchMove = (ev: TouchEvent): void => {
    if (!this.isLeftHandlerDown && !this.isRightHandlerDown) {
      return;
    }

    const touchEvent = ev.changedTouches[0];
    const handler = this.currentHandler();

    const relativeX = this.calculateNextRelativeX({
      eventX: touchEvent!.clientX,
      handler,
    });

    this.updateValue(relativeX, handler);
  };

  checkCorrectProps = () => {
    const {min, max, values} = this.props;
    checkValidMinMaxProps({min, max});
    checkValidValues(values);
  };

  render() {
    this.checkCorrectProps();
    const {leftHandlerX, rightHandlerX} = this.getHandlerPositions();
    const valueSource = this.getValues();
    const [minSelectedValue, maxSelectedValue] = valueSource;
    const progressBarStyle = getProgressBarStyle({
      leftHandlerX,
      rightHandlerX,
    });
    const mightHaveCollusion = rightHandlerX - leftHandlerX < 20;

    const {showLabels, className} = this.props;
    const minSelectedLabel = this.props.transformLabel(minSelectedValue);
    const maxSelectedLabel = this.props.transformLabel(maxSelectedValue);

    return (
      <Wrapper
        data-testid="PriceSlider"
        ref={this.containerRef}
        onMouseDown={this.handleSliderMouseDown}
        className={className}
      >
        <Background />
        <Progress style={progressBarStyle} />

        <TouchControl
          data-testid="Start"
          style={{left: `${leftHandlerX}%`}}
          onMouseDown={this.handleLeftHandlerMouseDown}
          onTouchStart={this.handleLeftHandlerTouchStart}
          aria-label={showLabels ? minSelectedLabel : undefined}
          moveLeft={mightHaveCollusion}
          moveRight={leftHandlerX < 5 && !mightHaveCollusion}
          contentLength={minSelectedLabel.length}
        />
        <TouchControl
          data-testid="End"
          style={{left: `${rightHandlerX}%`}}
          onMouseDown={this.handleRightHandlerMouseDown}
          onTouchStart={this.handleRightHandlerTouchStart}
          aria-label={showLabels ? maxSelectedLabel : undefined}
          moveRight={mightHaveCollusion}
          moveLeft={rightHandlerX > 95 && !mightHaveCollusion}
        />
      </Wrapper>
    );
  }
}

const TouchControl = styled.div<{
  contentLength?: number;
  moveRight?: boolean;
  moveLeft?: boolean;
}>`
  position: absolute;
  width: 40px;
  background-color: transparent;
  height: 40px;
  margin-left: -20px;

  &::before {
    content: '';
    display: block;
    width: 22px;
    height: 22px;
    margin: 8px;
    border-radius: 50%;
    background-color: ${COLORS.White};
    box-shadow: 0 2px 2px rgba(0, 0, 0, 0.24), 0 0 2px rgba(0, 0, 0, 0.12);
  }
`;

const Progress = styled.div`
  position: absolute;
  top: 18px;
  width: 100%;
  height: 4px;
  background-color: ${COLORS.Green};
`;

const Background = styled.div`
  position: absolute;
  margin: auto;
  top: 0;
  bottom: 0;
  width: 100%;
  height: 4px;
  background-color: ${COLORS.Grey3};
`;

const Wrapper = styled.div`
  position: relative;
  width: calc(100% - 24px);
  height: 40px;
  cursor: pointer;
`;
